import React from 'react';
import { Button } from 'react-covideo-common';
import styled from 'styled-components/macro';
import { NewModal } from 'lib/components';
import { theme } from 'lib/style';
import { useDeleteVideoMutation } from 'lib/api/videos/useDeleteVideoMutation';
import { useUpdateVideoArchiveStatusMutation } from 'lib/api/videos/useUpdateVideoArchiveStatusMutation';
import { useAuth, VideoListAutomotiveItem } from 'lib/context';
import { VideoData } from 'lib/hooks';
import { DEFAULT_COMPANY_FOLDER } from 'lib/const/Folders';
import { Folder } from 'lib/api/folders/getFolders';
import { useDeleteVideoWithROMutation } from 'lib/api/videos/useDeleteVideoWithROMutation';
import { trackVideoActionEvent } from 'lib/utils/pushToDataLayer';

const Content = styled.div`
  width: 600px;
  line-height: 28px;
  color: #272a32;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

type Props = {
  handleModalClose: (shouldRefresh?: boolean) => void;
  content: any;
  isCompanyVideo: boolean;
  video: VideoListAutomotiveItem[] | VideoData[];
  isVideoWithRO?: boolean;
  isMultipleVideos?: boolean;
  currentFolder?: Folder | null;
  isFolderShared?: boolean;
};

export const DeleteVideoModal = (props: Props) => {
  const {
    handleModalClose,
    content,
    isVideoWithRO,
    video,
    currentFolder,
    isMultipleVideos,
    isFolderShared,
    isCompanyVideo,
  } = props;

  const [isDisabled, setIsDisabled] =
    React.useState(false); /** SUS-973 changes **/
  const { userData } = useAuth();

  const { isAutomotive } = userData;

  const areAllVideosArchived = (
    video as Array<VideoListAutomotiveItem | VideoData>
  ).every(v => v.isArchived === 1);

  const { mutateAsync: deleteVideos } = useDeleteVideoMutation();
  const { mutateAsync: deleteVideoWithRO } = useDeleteVideoWithROMutation();
  const { mutateAsync: updateVideoArchiveStatus } =
    useUpdateVideoArchiveStatusMutation();

  const handleDeleteSubmitSelected = async (
    selectedVideosData: VideoListAutomotiveItem[]
  ) => {
    const hasUserCopyAccess = currentFolder?.access === 'copy';
    const hasUserViewAccess = currentFolder?.access === 'view';
    if (isAutomotive && currentFolder?.name !== DEFAULT_COMPANY_FOLDER) {
      for (const video of selectedVideosData) {
        if (video?.videoRequest?.videoRequestId) {
          await deleteVideoWithRO(video.videoRequest.videoRequestId);
        }
      }
    } else {
      await handleDeleteSubmit(
        selectedVideosData.map((video: VideoListAutomotiveItem) => video.id),
        isFolderShared
          ? hasUserCopyAccess || hasUserViewAccess
          : currentFolder?.name === DEFAULT_COMPANY_FOLDER
      );
    }
  };

  const handleDeleteSubmit = async (
    videoIds: string[],
    companyVideos: boolean = false
  ) => {
    await deleteVideos({ videoIds, deleteMultiple: true, companyVideos });
  };

  const handleDeleteWithROSubmit = async (videoId: string) => {
    await deleteVideoWithRO(videoId);
  };

  const handleArchiveSubmit = async (videoIds: number[]) => {
    updateVideoArchiveStatus({
      videoIds,
      isArchived: true,
    });
  };

  const deleteSelectedVideos = async () => {
    if (isVideoWithRO) {
      await handleDeleteWithROSubmit(
        (video[0] as VideoListAutomotiveItem)?.videoRequest?.videoRequestId
      );
      trackVideoActionEvent({
        event: 'Video Interaction',
        videoAction: 'Delete',
        video: video[0] as VideoListAutomotiveItem,
        userData,
      });
      return;
    }
    if (isMultipleVideos) {
      await handleDeleteSubmitSelected(video as VideoListAutomotiveItem[]);
      return;
    }
    await handleDeleteSubmit([video[0].id]);
    trackVideoActionEvent({
      event: 'Video Interaction',
      videoAction: 'Delete',
      video: video[0] as VideoListAutomotiveItem,
      userData,
    });
    handleModalClose();
  };

  return (
    <NewModal
      headerText={
        isCompanyVideo
          ? 'Are you sure you want to delete this company video?'
          : 'Are you sure you want to move this video to recently deleted bin?'
      }
      headerTextStyle={{
        fontSize: theme.fontSizes.lg,
        lineHeight: '24px',
        color: theme.palette.themeDark,
      }}
      closeModalOnBackdropOrEscPress={true}
      closeModal={() => {
        handleModalClose();
      }}
      style={{
        content: {
          padding: '32px',
          minWidth: 'auto',
        },
      }}
    >
      <Content>{!isCompanyVideo && content}</Content>
      <Footer>
        <Button
          text={areAllVideosArchived ? 'Cancel' : 'Delete'}
          variant='secondary'
          onClick={async () => {
            if (areAllVideosArchived) {
              handleModalClose();
              return;
            }
            await deleteSelectedVideos();
          }}
        />
        <Button
          text={areAllVideosArchived ? 'Delete' : 'Archive'}
          onClick={async () => {
            if (areAllVideosArchived) {
              deleteSelectedVideos();
              return;
            }
            setIsDisabled(true); /** SUS-973 changes **/
            handleArchiveSubmit(
              video.map((videoItem: VideoListAutomotiveItem | VideoData) =>
                Number(videoItem.id)
              )
            );
            handleModalClose();
          }}
          disabled={isDisabled} /** SUS-973 changes **/
        />
      </Footer>
    </NewModal>
  );
};
